import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FolderOpen, 
  MoreHorizontal, 
  Trash2, 
  Edit,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  ListTodo,
  Calendar as CalendarIcon,
  Users,
  MessageSquare,
  FileText,
  Eye,
  Send
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const { token, user, hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [showDocDetailModal, setShowDocDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditingDoc, setIsEditingDoc] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [projectTransmittals, setProjectTransmittals] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [revisionCodes, setRevisionCodes] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [approvalCodes, setApprovalCodes] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [docEditForm, setDocEditForm] = useState({
    name: '',
    description: '',
    document_number: '',
    document_type_id: '',
    revision_code_id: '',
    discipline_id: '',
    approval_code_id: '',
    status_code_id: '',
    asset_ids: [],
    tags: []
  });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active'
  });
  const [taskFormData, setTaskFormData] = useState({
    name: '',
    description: '',
    assignee_id: '',
    start_date: '',
    end_date: '',
    status: 'todo',
    priority: 'medium'
  });

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'bg-green-500/10 text-green-500' },
    { value: 'on_hold', label: 'On Hold', color: 'bg-yellow-500/10 text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'bg-blue-500/10 text-blue-500' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/10 text-red-500' }
  ];

  const taskStatusOptions = [
    { value: 'todo', label: 'To Do' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'review', label: 'In Review' },
    { value: 'done', label: 'Done' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-blue-500/10 text-blue-500' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500/10 text-yellow-500' },
    { value: 'high', label: 'High', color: 'bg-red-500/10 text-red-500' }
  ];

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load users');
    }
  }, [token]);

  const fetchProjectDetails = useCallback(async (projectId) => {
    try {
      const [projectRes, tasksRes, commentsRes, docsRes, transmittalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/tasks?project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/comments?entity_type=project&entity_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/documents?project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/transmittals?project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSelectedProject(projectRes.data);
      setTasks(tasksRes.data.tasks);
      setComments(commentsRes.data.comments);
      setProjectDocuments(docsRes.data.documents);
      setProjectTransmittals(transmittalsRes.data.transmittals);
    } catch (error) {
      toast.error('Failed to load project details');
    }
  }, [token]);

  const fetchDocMetadata = useCallback(async () => {
    try {
      const [typesRes, revCodesRes, discRes, appCodesRes, statusCodesRes, assetsRes] = await Promise.all([
        axios.get(`${API_URL}/api/document-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/revision-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/disciplines`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/approval-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/status-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/assets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDocumentTypes(typesRes.data.document_types);
      setRevisionCodes(revCodesRes.data.revision_codes);
      setDisciplines(discRes.data.disciplines);
      setApprovalCodes(appCodesRes.data.approval_codes);
      setStatusCodes(statusCodesRes.data.status_codes);
      setAssets(assetsRes.data.assets);
    } catch (error) {
      console.error('Failed to load document metadata');
    }
  }, [token]);

  const fetchDocumentDetail = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDoc(response.data);
      setDocEditForm({
        name: response.data.name || '',
        description: response.data.description || '',
        document_number: response.data.document_number || '',
        document_type_id: response.data.document_type_id || '',
        revision_code_id: response.data.revision_code_id || '',
        discipline_id: response.data.discipline_id || '',
        approval_code_id: response.data.approval_code_id || '',
        status_code_id: response.data.status_code_id || '',
        asset_ids: response.data.asset_ids || [],
        tags: response.data.tags || []
      });
      setShowDocDetailModal(true);
    } catch (error) {
      toast.error('Failed to load document details');
    }
  };

  const handleSaveDocEdit = async () => {
    if (!selectedDoc) return;
    setSavingDoc(true);
    try {
      await axios.put(`${API_URL}/api/documents/${selectedDoc.id}`, {
        ...docEditForm,
        document_type_id: docEditForm.document_type_id || null,
        revision_code_id: docEditForm.revision_code_id || null,
        discipline_id: docEditForm.discipline_id || null,
        approval_code_id: docEditForm.approval_code_id || null,
        status_code_id: docEditForm.status_code_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document updated');
      setIsEditingDoc(false);
      // Refresh document and project documents list
      fetchDocumentDetail(selectedDoc.id);
      if (selectedProject) {
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setSavingDoc(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchDocMetadata();
  }, [fetchProjects, fetchUsers, fetchDocMetadata]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/projects`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await axios.put(`${API_URL}/api/projects/${selectedProject.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await axios.post(`${API_URL}/api/tasks`, {
        ...taskFormData,
        project_id: selectedProject.id,
        assignee_id: taskFormData.assignee_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task created successfully');
      setShowTaskModal(false);
      resetTaskForm();
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task updated');
      if (selectedProject) {
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      if (selectedProject) {
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedProject) return;

    try {
      await axios.post(`${API_URL}/api/comments`, {
        content: newComment,
        entity_type: 'project',
        entity_id: selectedProject.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment('');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      status: 'active'
    });
    setSelectedProject(null);
  };

  const resetTaskForm = () => {
    setTaskFormData({
      name: '',
      description: '',
      assignee_id: '',
      start_date: '',
      end_date: '',
      status: 'todo',
      priority: 'medium'
    });
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      status: project.status
    });
    setShowEditModal(true);
  };

  const openProjectDetail = (project) => {
    setSelectedProject(project);
    fetchProjectDetails(project.id);
    setShowProjectDetail(true);
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = priorityOptions.find(p => p.value === priority) || priorityOptions[1];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Simple Gantt Chart Component
  const GanttChart = ({ tasks }) => {
    if (!tasks.length) return <p className="text-muted-foreground text-center py-8">No tasks with dates</p>;
    
    const tasksWithDates = tasks.filter(t => t.start_date && t.end_date);
    if (!tasksWithDates.length) return <p className="text-muted-foreground text-center py-8">No tasks with dates</p>;

    const allDates = tasksWithDates.flatMap(t => [parseISO(t.start_date), parseISO(t.end_date)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    const totalDays = differenceInDays(maxDate, minDate) + 1;

    return (
      <div className="space-y-2">
        {tasksWithDates.map((task) => {
          const startOffset = differenceInDays(parseISO(task.start_date), minDate);
          const duration = differenceInDays(parseISO(task.end_date), parseISO(task.start_date)) + 1;
          const startPercent = (startOffset / totalDays) * 100;
          const widthPercent = (duration / totalDays) * 100;

          const statusColors = {
            todo: 'bg-muted',
            in_progress: 'bg-blue-500',
            review: 'bg-yellow-500',
            done: 'bg-green-500'
          };

          return (
            <div key={task.id} className="flex items-center gap-4">
              <div className="w-32 text-sm truncate">{task.name}</div>
              <div className="flex-1 h-8 bg-muted/50 rounded relative">
                <div
                  className={`gantt-bar-fill ${statusColors[task.status] || 'bg-muted'}`}
                  style={{
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your projects and tasks
          </p>
        </div>
        {hasPermission('manage_projects') && (
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      <div className="p-8">
        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-projects"
            />
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No projects found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openProjectDetail(project)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <FolderOpen className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{project.name}</CardTitle>
                      {getStatusBadge(project.status)}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {hasPermission('manage_projects') && (
                        <>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(project); }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  {project.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        {project.task_count > 0 
                          ? `${Math.round((project.completed_tasks / project.task_count) * 100)}%`
                          : '0%'
                        }
                      </span>
                    </div>
                    <Progress 
                      value={project.task_count > 0 ? (project.completed_tasks / project.task_count) * 100 : 0} 
                      className="h-2"
                    />
                    
                    <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{project.document_count || 0} docs</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ListTodo className="w-4 h-4" />
                        <span>{project.task_count || 0} tasks</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Send className="w-4 h-4" />
                        <span>{project.transmittal_count || 0} transmittals</span>
                      </div>
                      {project.end_date && (
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{format(new Date(project.end_date), 'MMM d')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-project-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Project Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-start_date">Start Date</Label>
                <Input
                  id="edit-start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end_date">End Date</Label>
                <Input
                  id="edit-end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Detail Modal */}
      <Dialog open={showProjectDetail} onOpenChange={setShowProjectDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedProject.name}</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">{selectedProject.description}</p>
                  </div>
                  {getStatusBadge(selectedProject.status)}
                </div>
              </DialogHeader>

              <Tabs defaultValue="documents" className="mt-4">
                <TabsList>
                  <TabsTrigger value="documents">
                    <FileText className="w-4 h-4 mr-2" />
                    Documents ({projectDocuments.length})
                  </TabsTrigger>
                  <TabsTrigger value="transmittals">
                    <Send className="w-4 h-4 mr-2" />
                    Transmittals ({projectTransmittals.length})
                  </TabsTrigger>
                  <TabsTrigger value="tasks">
                    <ListTodo className="w-4 h-4 mr-2" />
                    Tasks
                  </TabsTrigger>
                  <TabsTrigger value="gantt">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Timeline
                  </TabsTrigger>
                  <TabsTrigger value="comments">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Comments
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="documents" className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Documents linked to this project</h3>
                  </div>
                  
                  {projectDocuments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No documents linked to this project</p>
                      <p className="text-xs mt-1">Link documents to this project from the Documents page</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Doc Number</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Type</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Revision</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Created</th>
                            <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {projectDocuments.map((doc) => (
                            <tr key={doc.id} className="hover:bg-muted/30 transition-colors">
                              <td className="p-3">
                                <button 
                                  className="font-mono text-sm text-primary hover:underline cursor-pointer"
                                  onClick={() => fetchDocumentDetail(doc.id)}
                                  data-testid={`doc-number-${doc.id}`}
                                >
                                  {doc.document_number || '-'}
                                </button>
                              </td>
                              <td className="p-3">
                                <div>
                                  <button 
                                    className="font-medium text-sm text-primary hover:underline cursor-pointer text-left"
                                    onClick={() => fetchDocumentDetail(doc.id)}
                                    data-testid={`doc-name-${doc.id}`}
                                  >
                                    {doc.name}
                                  </button>
                                  {doc.description && (
                                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.description}</p>
                                  )}
                                </div>
                              </td>
                              <td className="p-3">
                                {doc.document_type ? (
                                  <Badge variant="outline" className="text-xs">{doc.document_type.code}</Badge>
                                ) : '-'}
                              </td>
                              <td className="p-3">
                                <Badge className={
                                  doc.status === 'approved' ? 'bg-green-500/10 text-green-600' :
                                  doc.status === 'draft' ? 'bg-gray-500/10 text-gray-600' :
                                  doc.status === 'pending_approval' ? 'bg-yellow-500/10 text-yellow-600' :
                                  doc.status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                                  'bg-blue-500/10 text-blue-600'
                                }>
                                  {doc.status?.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {doc.revision_code ? (
                                  <span className="text-sm">{doc.revision_code.code}</span>
                                ) : '-'}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {format(new Date(doc.created_at), 'MMM d, yyyy')}
                              </td>
                              <td className="p-3 text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => fetchDocumentDetail(doc.id)}
                                  data-testid={`view-doc-${doc.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="transmittals" className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Transmittals linked to this project</h3>
                  </div>
                  
                  {projectTransmittals.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Send className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No transmittals linked to this project</p>
                      <p className="text-xs mt-1">Link transmittals to this project from the Transmittals page</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Number</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Direction</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Send Date</th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Documents</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {projectTransmittals.map((transmittal) => (
                            <tr key={transmittal.id} className="hover:bg-muted/30 transition-colors">
                              <td className="p-3 font-mono text-sm">{transmittal.transmittal_number}</td>
                              <td className="p-3">
                                <Badge className={transmittal.direction === 'outgoing' ? 'bg-blue-500/10 text-blue-600' : 'bg-green-500/10 text-green-600'}>
                                  {transmittal.direction === 'outgoing' ? '↗ Outgoing' : '↙ Incoming'}
                                </Badge>
                              </td>
                              <td className="p-3 text-sm">{transmittal.contact?.name || '-'}</td>
                              <td className="p-3">
                                {transmittal.status ? (
                                  <Badge style={{ backgroundColor: `${transmittal.status.color}20`, color: transmittal.status.color }}>
                                    {transmittal.status.name}
                                  </Badge>
                                ) : '-'}
                              </td>
                              <td className="p-3 text-sm">
                                {transmittal.send_date ? format(new Date(transmittal.send_date), 'MMM d, yyyy') : '-'}
                              </td>
                              <td className="p-3">
                                <Badge variant="outline">{transmittal.document_count || 0} docs</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="tasks" className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Tasks ({tasks.length})</h3>
                    <Button size="sm" onClick={() => setShowTaskModal(true)} data-testid="add-task-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Task
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {tasks.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No tasks yet</p>
                    ) : (
                      tasks.map((task) => (
                        <div 
                          key={task.id} 
                          className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Select
                              value={task.status}
                              onValueChange={(value) => handleUpdateTask(task.id, { status: value })}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {taskStatusOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div>
                              <p className="font-medium text-sm">{task.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {task.assignee_name || 'Unassigned'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getPriorityBadge(task.priority)}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="gantt" className="mt-4">
                  <GanttChart tasks={tasks} />
                </TabsContent>

                <TabsContent value="comments" className="mt-4">
                  <form onSubmit={handleAddComment} className="flex gap-2 mb-4">
                    <Input
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      data-testid="comment-input"
                    />
                    <Button type="submit" disabled={!newComment.trim()}>
                      Post
                    </Button>
                  </form>
                  
                  <div className="space-y-3">
                    {comments.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No comments yet</p>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{comment.created_by_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(comment.created_at), 'MMM d, HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-name">Task Name</Label>
              <Input
                id="task-name"
                value={taskFormData.name}
                onChange={(e) => setTaskFormData({ ...taskFormData, name: e.target.value })}
                required
                data-testid="task-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={taskFormData.description}
                onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-assignee">Assignee</Label>
                <Select
                  value={taskFormData.assignee_id || "unassigned"}
                  onValueChange={(value) => setTaskFormData({ ...taskFormData, assignee_id: value === "unassigned" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-priority">Priority</Label>
                <Select
                  value={taskFormData.priority}
                  onValueChange={(value) => setTaskFormData({ ...taskFormData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-start">Start Date</Label>
                <Input
                  id="task-start"
                  type="date"
                  value={taskFormData.start_date}
                  onChange={(e) => setTaskFormData({ ...taskFormData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-end">Due Date</Label>
                <Input
                  id="task-end"
                  type="date"
                  value={taskFormData.end_date}
                  onChange={(e) => setTaskFormData({ ...taskFormData, end_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowTaskModal(false); resetTaskForm(); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-task-submit">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Detail Modal */}
      <Dialog open={showDocDetailModal} onOpenChange={(open) => {
        setShowDocDetailModal(open);
        if (!open) {
          setIsEditingDoc(false);
          setSelectedDoc(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedDoc && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedDoc.name}</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1 font-mono">{selectedDoc.document_number}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      selectedDoc.status === 'approved' ? 'bg-green-500/10 text-green-600' :
                      selectedDoc.status === 'draft' ? 'bg-gray-500/10 text-gray-600' :
                      selectedDoc.status === 'pending_approval' ? 'bg-yellow-500/10 text-yellow-600' :
                      selectedDoc.status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                      'bg-blue-500/10 text-blue-600'
                    }>
                      {selectedDoc.status?.replace('_', ' ')}
                    </Badge>
                    {hasPermission('edit_documents') && !isEditingDoc && (
                      <Button variant="outline" size="sm" onClick={() => setIsEditingDoc(true)} data-testid="edit-doc-btn">
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    {isEditingDoc && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setIsEditingDoc(false)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveDocEdit} disabled={savingDoc} data-testid="save-doc-btn">
                          {savingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Name</Label>
                    {isEditingDoc ? (
                      <Input
                        value={docEditForm.name}
                        onChange={(e) => setDocEditForm({ ...docEditForm, name: e.target.value })}
                        data-testid="doc-edit-name"
                      />
                    ) : (
                      <p className="font-medium">{selectedDoc.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Document Number</Label>
                    {isEditingDoc ? (
                      <Input
                        value={docEditForm.document_number}
                        onChange={(e) => setDocEditForm({ ...docEditForm, document_number: e.target.value })}
                        data-testid="doc-edit-number"
                      />
                    ) : (
                      <p className="font-mono">{selectedDoc.document_number || '-'}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase">Description</Label>
                  {isEditingDoc ? (
                    <Textarea
                      value={docEditForm.description}
                      onChange={(e) => setDocEditForm({ ...docEditForm, description: e.target.value })}
                      rows={2}
                      data-testid="doc-edit-desc"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">{selectedDoc.description || '-'}</p>
                  )}
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Document Type</Label>
                    {isEditingDoc ? (
                      <Select
                        value={docEditForm.document_type_id || "none"}
                        onValueChange={(value) => setDocEditForm({ ...docEditForm, document_type_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {documentTypes.map((dt) => (
                            <SelectItem key={dt.id} value={dt.id}>{dt.code} - {dt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{selectedDoc.document_type ? `${selectedDoc.document_type.code} - ${selectedDoc.document_type.name}` : '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Revision Code</Label>
                    {isEditingDoc ? (
                      <Select
                        value={docEditForm.revision_code_id || "none"}
                        onValueChange={(value) => setDocEditForm({ ...docEditForm, revision_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select revision" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {revisionCodes.map((rc) => (
                            <SelectItem key={rc.id} value={rc.id}>{rc.code} - {rc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{selectedDoc.revision_code ? `${selectedDoc.revision_code.code} - ${selectedDoc.revision_code.name}` : '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Discipline</Label>
                    {isEditingDoc ? (
                      <Select
                        value={docEditForm.discipline_id || "none"}
                        onValueChange={(value) => setDocEditForm({ ...docEditForm, discipline_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select discipline" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {disciplines.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.code} - {d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{selectedDoc.discipline ? `${selectedDoc.discipline.code} - ${selectedDoc.discipline.name}` : '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Approval Code</Label>
                    {isEditingDoc ? (
                      <Select
                        value={docEditForm.approval_code_id || "none"}
                        onValueChange={(value) => setDocEditForm({ ...docEditForm, approval_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select approval" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {approvalCodes.map((ac) => (
                            <SelectItem key={ac.id} value={ac.id}>{ac.code} - {ac.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{selectedDoc.approval_code ? `${selectedDoc.approval_code.code} - ${selectedDoc.approval_code.name}` : '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Status Code</Label>
                    {isEditingDoc ? (
                      <Select
                        value={docEditForm.status_code_id || "none"}
                        onValueChange={(value) => setDocEditForm({ ...docEditForm, status_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {statusCodes.map((sc) => (
                            <SelectItem key={sc.id} value={sc.id}>{sc.code} - {sc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">{selectedDoc.status_code ? `${selectedDoc.status_code.code} - ${selectedDoc.status_code.name}` : '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Version</Label>
                    <p className="font-medium">v{selectedDoc.version}</p>
                  </div>
                </div>

                {/* Creator and Dates */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">Creator</Label>
                    <p className="font-medium mt-1">{selectedDoc.created_by_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">Created</Label>
                    <p className="font-medium mt-1">{format(new Date(selectedDoc.created_at), 'MMM d, yyyy HH:mm')}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">Updated</Label>
                    <p className="font-medium mt-1">{format(new Date(selectedDoc.updated_at), 'MMM d, yyyy HH:mm')}</p>
                  </div>
                </div>

                {/* Linked Assets */}
                {(selectedDoc.linked_assets?.length > 0 || isEditingDoc) && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Linked Assets</Label>
                    {isEditingDoc ? (
                      <div className="border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2">
                        {assets.map((asset) => (
                          <label key={asset.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={docEditForm.asset_ids.includes(asset.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDocEditForm({ ...docEditForm, asset_ids: [...docEditForm.asset_ids, asset.id] });
                                } else {
                                  setDocEditForm({ ...docEditForm, asset_ids: docEditForm.asset_ids.filter(id => id !== asset.id) });
                                }
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">{asset.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedDoc.linked_assets?.map((asset) => (
                          <Badge key={asset.id} variant="outline">{asset.name}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tags */}
                {(selectedDoc.tags?.length > 0 || isEditingDoc) && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">Tags</Label>
                    {isEditingDoc ? (
                      <Input
                        value={docEditForm.tags.join(', ')}
                        onChange={(e) => setDocEditForm({ ...docEditForm, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                        placeholder="Enter tags separated by commas"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedDoc.tags?.map((tag, i) => (
                          <Badge key={i} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
